/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { CategoriesAPI, protectedCategories } from "../../../../api/Categories";
import { CategoriesAtom } from "../../../../atoms/CategoriesAtom";
import { TagsAtom } from "../../../../atoms/TagsAtom";
import { ICategoryType } from "../../../../generated/Api";
import { ICategory } from "../../../../types/CategoryTypes";
import { ITag } from "../../../../types/TagTypes";
import StyledButton from "../../../StyledButton";
import StyledInput from "../../../StyledInput";
import { useModal } from "../../Modal";
interface Props {
  onCancel: () => void;
  category?: ICategory;
  isEdit?: boolean;
}

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 30px 0;
  width: 400px;
  height: 100px;
`;

const Buttons = styled.div`
  margin: 0 -25px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #dddddd;
  padding: 20px 20px 0px;
`;

export default function CreateOrUpdateCategory(props: Props): JSX.Element {
  const { onCancel, isEdit, category } = props;
  const [tags, setTags] = useRecoilState<ITag[]>(TagsAtom);
  const [currentCategories, setCurrentCategories] =
    useRecoilState(CategoriesAtom);
  const [currentCategory, setCategory] = useState<Partial<ICategory>>(
    category
      ? {
          _id: category?._id,
          name: category?.name,
          type: category?.type,
        }
      : {
          name: "",
          type: ICategoryType.User,
        }
  );
  const modal = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function createCategory() {
    setIsLoading(true);
    const foundSameCategory = tags.find(
      (t) =>
        t.category?.toLowerCase().replace(/\s\s+/g, " ").trim() ===
        currentCategory.name?.replace(/\s\s+/g, " ").toLowerCase().trim()
    );

    const isProtected = protectedCategories.find(
      (c) =>
        c.toLowerCase().replace(/\s\s+/g, " ").trim() ===
        currentCategory.name?.toLowerCase().replace(/\s\s+/g, " ").trim()
    );

    if (!currentCategory.name?.replace(/\s\s+/g, " ").trim()) {
      toast("Can't create a category with empty name", { type: "info" });
    } else if (foundSameCategory) {
      toast("Can't create category with same name", { type: "info" });
    } else if (isProtected) {
      toast("Can't create category with system category name", {
        type: "info",
      });
    } else {
      try {
        const categoryResponse = await CategoriesAPI.create({
          name: currentCategory.name.replace(/\s\s+/g, " ").trim(),
          type: ICategoryType.User,
        });

        if (categoryResponse) {
          setCurrentCategories([...currentCategories, categoryResponse]);
          toast("Category created succefully", { type: "success" });
        }
      } catch (error) {
        toast("There was an error, try again later", { type: "error" });
      } finally {
        modal.popModal();
      }
    }
    setIsLoading(false);
  }

  async function updateCategory() {
    setIsLoading(true);
    const foundSameCategory = tags.find(
      (t) =>
        t.category?.toLowerCase().replace(/\s\s+/g, " ").trim() ===
        currentCategory.name?.toLowerCase().replace(/\s\s+/g, " ").trim()
    );

    const isProtected = protectedCategories.find(
      (c) =>
        c.toLowerCase().replace(/\s\s+/g, " ").trim() ===
        currentCategory.name?.toLowerCase().replace(/\s\s+/g, " ").trim()
    );

    if (!currentCategory.name?.replace(/\s\s+/g, " ").trim()) {
      toast("Can't update a category with empty name", { type: "info" });
    } else if (foundSameCategory) {
      toast("Can't upated a category with same name", { type: "info" });
    } else if (isProtected) {
      toast("Can't create category with system category name", {
        type: "info",
      });
    } else {
      try {
        let updatedCategory = await CategoriesAPI.update({
          _id: currentCategory._id!,
          name: currentCategory.name.replace(/\s\s+/g, " ").trim(),
          type: ICategoryType.User,
        });

        setCurrentCategories(
          currentCategories.map((c) =>
            updatedCategory._id === c._id ? updatedCategory : c
          )
        );
        setTags(
          tags.map((t) =>
            t.categoryId === updatedCategory._id
              ? { ...t, category: updatedCategory.name }
              : t
          )
        );
        toast("Category updated succefully", { type: "success" });
      } catch (error) {
        toast("There was an error, try again later", { type: "error" });
      } finally {
        modal.popModal();
      }
    }
    setIsLoading(false);
  }

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        if (isEdit) updateCategory();
        else createCategory();
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [currentCategory.name]);

  return (
    <>
      <InputWrapper>
        <Title>Category Name</Title>
        <StyledInput
          ref={inputRef}
          value={currentCategory.name}
          onChange={(e) =>
            setCategory({ ...currentCategory, name: e.target.value })
          }
          placeholder="Enter the category name"
          css={css`
            margin-top: 5px;
          `}
        />
      </InputWrapper>

      <Buttons
        css={css`
          display: flex;
          justify-content: flex-end;
        `}
      >
        <StyledButton
          onClick={onCancel}
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
        >
          Cancel
        </StyledButton>
        <StyledButton
          css={css`
            width: 80px;
          `}
          isLoading={isLoading}
          onClick={() => {
            if (isEdit) updateCategory();
            else createCategory();
          }}
        >
          Save
        </StyledButton>
      </Buttons>
    </>
  );
}
